// telemetry-transport — o CANO da telemetria. Fire-and-forget de verdade.
//
// REGRA DO DONO: "telemetria deveria ser fire-and-forget, não pode impactar a
// performance". `net-priority` garante QUANDO a telemetria sai; este módulo
// garante COMO ela sai — e, principalmente, o que ela NÃO pode fazer.
//
// ===== POR QUE A TELEMETRIA NÃO USA O `request()` DO api.ts =====
//
// O `request()` é o cliente do APP: ele carrega comportamentos que são certos
// pro catálogo e ERRADOS (perigosos) pra telemetria:
//
//  1. FAILOVER + RETRY-STORM: em 5xx/erro de rede ele roda `rotateApiUrl()` e
//     re-tenta, até 10 tentativas. Um flush de QoE que falha viraria 10 fetches
//     — e, pior, **rotacionaria o endpoint ativo do APP INTEIRO** (o pointer do
//     runtime-config é global). Ou seja: uma falha de TELEMETRIA podia mudar
//     pra qual backend o PLAYBACK vai pedir sign. Telemetria nunca pode ter
//     esse poder. Aqui: 1 tentativa, sem rotação, falha engolida.
//
//  2. REFRESH QUE DESLOGA: `request({auth:true})` → `swipeAuthHeader()` →
//     `refreshIfNeeded()`, cujo catch faz `clearTokens() + notifyAuthInvalid()`
//     no caminho Xtream legado. Rede caída + flush de QoE = **usuário
//     deslogado por causa de telemetria**. Aqui o Bearer é resolvido sem NUNCA
//     poder disparar esse caminho (ver telemetryAuthToken).
//
// Invariantes deste módulo:
//   - NUNCA lança (todo caminho RETORNA, nenhum propaga exceção). O `postTelemetry`
//     devolve `TelemetryPostResult` — era boolean, e o booleano colapsava "tente de
//     novo" com "este corpo nunca será aceito", o que virava retry eterno no 413.
//     `postTelemetryUnload` segue boolean: no unload não existe próximo envio, então a
//     distinção não teria consumidor.
//   - NUNCA re-tenta, NUNCA rotaciona endpoint.
//   - NUNCA desloga o usuário.
//   - Serializa FORA do frame corrente (yieldToMain antes do JSON.stringify).

// A base vem do `getApiBase()` do api.ts — fonte única, resolvida pelo
// runtime-config no boot. Ler `import.meta.env` aqui seria o bug que deixou o
// catálogo da LG sem capa na 2.11.1 (URL root-relativa → `file:///v1/...` na TV
// empacotada); há um guard de classe pra isso em lib/api-base-source.test.ts.
//
// Note que usar `getApiBase()` dá ao transporte a base ATIVA (com o failover
// que o app já decidiu) sem lhe dar o poder de ROTACIONAR nada: só o `request()`
// do api.ts chama rotateApiUrl. Telemetria lê, não decide.
//
// O import cíclico api → qoe → telemetry-transport → api é seguro e igual ao que
// já existia (api → qoe → api): `getApiBase` é function declaration (hoisted) e
// só é chamada em runtime, nunca na avaliação do módulo.
import { getApiBase } from './api'
import { getTokens, isTokenExpired } from './auth-store'
import { yieldToMain } from './lib/idle-scheduler'
import { getValidSwipeAccessToken, hasSwipeSession } from './sync/swipe-session'

/**
 * Bearer pra telemetria — resolvido de forma NÃO-DESTRUTIVA.
 *
 * - Com sessão Swipe: `getValidSwipeAccessToken()` é seguro (rede caída NÃO
 *   desloga; só um 401 do refresh limpa a sessão, e isso é um refresh morto de
 *   verdade, não um efeito da telemetria).
 * - Sem sessão Swipe (Xtream legado): lê o token direto do store e **NÃO chama
 *   refreshIfNeeded()**. Se o token está expirado, a telemetria simplesmente
 *   desiste. Perder uma amostra de QoE é gratuito; deslogar o usuário porque a
 *   telemetria tentou renovar um token com a rede caída, não.
 */
async function telemetryAuthToken(): Promise<string | null> {
  if (hasSwipeSession()) return getValidSwipeAccessToken()
  if (isTokenExpired()) return null
  const tokens = getTokens()
  return tokens ? tokens.accessToken : null
}

/** AbortController existe no Chr66+; o piso da TV é Chr38. Feature-detect —
 *  sem ele, a telemetria vai sem timeout (fire-and-forget: ninguém espera). */
function makeTimeout(ms: number): { signal?: AbortSignal; clear: () => void } {
  if (typeof AbortController !== 'function') return { clear: () => {} }
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), ms)
  return { signal: ctrl.signal, clear: () => clearTimeout(timer) }
}

const TELEMETRY_TIMEOUT_MS = 10_000

/**
 * POST de telemetria. Devolve `{ ok, retryable }` — ver `TelemetryPostResult` pro
 * porquê de não ser mais um boolean. NUNCA lança, NUNCA re-tenta (o `retryable` só
 * INFORMA o chamador; quem decide reenviar é o `flush`), NUNCA rotaciona endpoint.
 *
 * A serialização acontece DEPOIS de um `yieldToMain()`: mesmo que o payload
 * seja grande (até 200 eventos de QoE), o `JSON.stringify` não roda dentro do
 * frame de quem chamou — ele cede a main thread primeiro. Chamadores já esperam
 * a janela crítica fechar (net-priority), então nem o stringify nem o fetch
 * gastam main thread/rede enquanto o usuário espera um frame.
 */
/**
 * Desfecho de um POST de telemetria.
 *
 * ⚠️ POR QUE ISTO NÃO É MAIS UM `boolean`. Era, e o booleano colapsava duas coisas
 * que precisam ser distinguidas: "falhou, tente de novo" e "este corpo NUNCA vai ser
 * aceito". Com as duas iguais, o chamador devolvia o lote ao buffer em ambos os casos —
 * e no segundo isso vira um LOOP: o mesmo lote é reenviado a cada backoff, recebe a
 * mesma recusa, e a telemetria daquela sessão para de fluir em SILÊNCIO. Foi o modo de
 * falha que o teto de bytes do servidor (`bodyLimit` do POST /v1/qoe) tornou alcançável.
 *
 * `retryable: false` significa: o problema está no CORPO, e reenviá-lo é garantia de
 * falhar de novo.
 */
export interface TelemetryPostResult {
  ok: boolean
  /** Reenviar este MESMO corpo tem chance de sucesso? */
  retryable: boolean
}

/**
 * Um status HTTP condena o CORPO (reenviar é inútil) ou é circunstancial?
 *
 * Condenam: 400 (malformado / acima do teto de eventos), 413 (acima do teto de bytes),
 * 422 e 403. Reenviar qualquer um deles produz exatamente a mesma resposta.
 *
 * ⚠️ O 403 ENTROU DEPOIS, e faltava: `/v1/qoe` emite `forbidden_token_type` (token de
 * painel numa rota de app) e `provider_inactive`. Nenhum dos dois se conserta por
 * reenvio — são estruturais, e sem isto o lote voltava pro buffer, armava o backoff e
 * era reenviado a cada 60s indefinidamente. É o MESMO loop silencioso que a distinção
 * `retryable` existe pra matar, um status ao lado dele.
 *
 * ⚠️ 429 É RETENTÁVEL, e a distinção importa: ele é o rate-limit por sessão do próprio
 * /v1/qoe (30/min). Tratá-lo como definitivo faria o cliente DESCARTAR telemetria
 * legítima toda vez que o teto fosse tocado — o oposto do que o teto existe pra fazer,
 * já que ele é barreira de VAZÃO, não veredito sobre o conteúdo.
 *
 * ⚠️ 401 FICA RETENTÁVEL, e há um caso em que isso vira loop — está dito aqui porque
 * quem for causá-lo precisa esbarrar nesta linha. O 401 normal é token expirado, que a
 * próxima tentativa renova, então re-tentar é o comportamento certo. MAS o authGuard
 * também responde 401 `account_required` quando a flag `SWIPE_ONLY_AUTH` (I7) está
 * ligada, e esse não é transitório: enquanto a flag estiver ligada, ele responde 401
 * para SEMPRE naquele token. Ou seja: LIGAR `SWIPE_ONLY_AUTH` PÕE TODO DEVICE LEGADO EM
 * RETRY DE 60s INDEFINIDO. Quem ligar a flag tem que tratar `account_required` como
 * condenado aqui, ou o parque legado inteiro passa a martelar a rota até ser atualizado.
 */
function corpoCondenado(status: number): boolean {
  return status === 400 || status === 403 || status === 413 || status === 422
}

export async function postTelemetry(
  path: string,
  payload: unknown,
  opts: { auth?: boolean } = {},
): Promise<TelemetryPostResult> {
  const { auth = true } = opts
  try {
    const token = auth ? await telemetryAuthToken() : null
    // sem identidade → descarta, sem barulho. NÃO é retentável: sem token o corpo não
    // chega a sair, e devolvê-lo ao buffer só o faria envelhecer ali.
    if (auth && !token) return { ok: false, retryable: false }

    // Cede a main thread ANTES de serializar: o custo de CPU do payload sai do
    // frame de quem chamou.
    await yieldToMain()
    const body = JSON.stringify(payload)

    const headers: Record<string, string> = { 'content-type': 'application/json' }
    if (token) headers['authorization'] = `Bearer ${token}`

    const { signal, clear } = makeTimeout(TELEMETRY_TIMEOUT_MS)
    try {
      const res = await fetch(`${getApiBase()}${path}`, {
        method: 'POST',
        headers,
        body,
        ...(signal ? { signal } : {}),
      })
      return { ok: res.ok, retryable: res.ok ? false : !corpoCondenado(res.status) }
    } finally {
      clear()
    }
  } catch {
    // Rede caída, DNS, abort, JSON circular — tudo engolido. Telemetria some,
    // app segue. Este catch é a invariante nº 3 do dono.
    // RETENTÁVEL: falha de transporte não diz nada sobre o corpo.
    return { ok: false, retryable: true }
  }
}

// ===== FIM DE SESSÃO =====
//
// O `postTelemetry` acima é CANCELADO quando a página descarrega — e é bem aí
// que está o dado mais caro de perder (a sessão que ainda não coube num flush de
// 30s). `keepalive: true` resolve as duas pontas de uma vez: o request SOBREVIVE
// ao unload E manda HEADERS — ou seja, manda o `Authorization: Bearer` que o
// `/v1/qoe` exige (authGuard em apps/api/src/routes/authenticated.ts).
//
// É por isso que aqui NÃO tem `navigator.sendBeacon`: beacon não manda header
// customizado, então nunca teve como autenticar no `/v1/qoe` — o envio de fim de
// sessão via beacon era, na prática, um 401. Nada muda no backend: o contrato
// (Bearer no header) continua o mesmo, o cliente é que passou a conseguir
// cumpri-lo no unload.
//
// Compat: keepalive é Chr66+/FF90+/Safari13+. O piso da TV (Chr38/53) NÃO tem —
// e não precisa: lá o app não "fecha aba", ele vai pra STANDBY
// (visibilitychange → hidden) ainda VIVO, e um fetch normal entrega. Por isso o
// corte é FEATURE-DETECT de capability, nunca `if (webOS)`.

/**
 * Teto do keepalive. A spec do Fetch dá 64 KiB de quota pra TODOS os requests
 * keepalive em voo do mesmo contexto — e estourar não dá exceção nem status: o
 * request vira network error e SOME. Um buffer de 200 eventos de QoE passa disso
 * com folga (health_sample/fps_drop carregam meta aberto).
 *
 * Orçamos 60 KiB pro body. A folga de 4 KiB cobre os headers (o Bearer JWT
 * sozinho passa de 1 KB), que implementações do Chromium já contaram dentro da
 * mesma quota. Mandar 190 eventos é melhor que perder os 200.
 */
export const KEEPALIVE_BUDGET_BYTES = 60 * 1024

/**
 * Teto de BYTES que o SERVIDOR aceita no corpo de `/v1/qoe` — 128 KiB (`bodyLimit` da
 * rota, contrato em docs/specs/api.md §7.4.3), menos margem. Distinto do
 * `KEEPALIVE_BUDGET_BYTES`: aquele é imposto pelo BROWSER (quota do keepalive), este
 * pelo BACKEND, e eles valem em caminhos diferentes.
 *
 * Vive aqui, e não em `qoe.ts`, porque os DOIS caminhos de envio precisam dele — o flush
 * periódico e o fim de sessão sem keepalive. Dois números pro mesmo teto é como a
 * divergência nasce.
 *
 * ⚠️ A MARGEM COBRE O ENVELOPE, NÃO UM QUARTO DO TETO. A primeira versão usou 96 KiB
 * (75%) por conservadorismo e isso REGREDIU um caso legítimo: um lote de 200 eventos
 * gordos mede ~104 KiB, cabe folgado nos 128 KiB do servidor, e mesmo assim era cortado
 * em 178 eventos — perda de dado que o backend teria aceitado. Um teste irmão que já
 * afirmava "não corta à toa" pegou. 8 KiB de margem cobrem o envelope
 * (`{"events":[]}` = 13 B) e qualquer diferença entre o tamanho medido aqui e o que o
 * `JSON.stringify` final produz, com ordens de grandeza de sobra.
 */
export const SERVER_BODY_BUDGET_BYTES = 120 * 1024

/**
 * Bytes UTF-8 — a quota do keepalive é em BYTES, e `String.length` conta code
 * units UTF-16. Em pt-BR a diferença não é detalhe: "Ação" tem length 4 e pesa 5
 * bytes; título com emoji pesa 4 por caractere. Contar errado é estourar a quota
 * achando que coube (e o request some sem avisar).
 */
function utf8Len(s: string): number {
  if (typeof TextEncoder === 'function') return new TextEncoder().encode(s).length
  let bytes = 0
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i)
    if (c < 0x80) bytes += 1
    else if (c < 0x800) bytes += 2
    else if (c >= 0xd800 && c <= 0xdbff) {
      bytes += 4
      i++ // par surrogate: o low já foi contado junto
    } else bytes += 3
  }
  return bytes
}

/** Tamanho do evento já serializado. `?? 'null'` porque JSON.stringify devolve
 *  `undefined` (não string) pra alguns valores — sem isso, utf8Len quebraria. */
/**
 * Tamanho em BYTES UTF-8 do valor serializado.
 *
 * ⚠️ EXPORTADO pra que o orçamento do flush periódico (`qoe.ts`) use ESTE medidor, e
 * não um segundo. A primeira versão daquele orçamento mediu com `JSON.stringify(x).length`
 * — que conta code units UTF-16, não bytes — e um título cheio de acentos (`ó` = 2 bytes
 * em UTF-8, 1 code unit) fazia o orçamento render o DOBRO: o lote saía com 175 KiB
 * debaixo de um teto de 96 KiB. Dois medidores da mesma grandeza divergem; um só, não.
 */
export function sizeOf(value: unknown): number {
  return utf8Len(JSON.stringify(value) ?? 'null')
}

/** `keepalive` existe no Chr66+/FF90+/Safari13+; no piso da TV (Chr38/53) não.
 *  Checa em `Request.prototype` (é lá que mora o getter) em vez de construir um
 *  `new Request()` só pra perguntar — construir custa e pode lançar. */
function supportsKeepalive(): boolean {
  try {
    return typeof Request === 'function' && 'keepalive' in Request.prototype
  } catch {
    return false
  }
}

/**
 * Corta o batch pra caber na quota do keepalive. FUNÇÃO PURA — é o ponto que o
 * teste de truncagem exercita direto, sem rede nem DOM.
 *
 * Mantém os eventos MAIS RECENTES (fim do array): mesma política do `track()`
 * quando o buffer estoura — amostra de 30min atrás vale menos que a de agora, e
 * o fim de sessão (o motivo deste envio existir) está sempre no fim.
 *
 * Truncou → `makeMarker` entra no batch. É o que impede a perda de ser
 * SILENCIOSA: sem o marcador, uma sessão que mandou 190 de 200 eventos é
 * indistinguível no Grafana de uma que só gerou 190. O marcador é um evento
 * comum do `events[]`, então o backend registra o rastro sem precisar mudar nada
 * (`QoeBody` aceita `z.record(z.unknown())` e grava `type` + payload).
 */
export function fitKeepaliveBatch<T>(
  events: readonly T[],
  makeMarker: (dropped: number, kept: number) => T,
  budgetBytes: number = KEEPALIVE_BUDGET_BYTES,
): { events: T[]; dropped: number } {
  if (events.length === 0) return { events: [], dropped: 0 }
  if (sizeOf({ events }) <= budgetBytes) return { events: [...events], dropped: 0 }

  // Reserva o marcador pelo PIOR caso (`dropped` = o batch inteiro): o número
  // real nunca tem mais dígitos que esse, então o batch final nunca fica maior
  // que a conta feita aqui.
  const reserved = sizeOf(makeMarker(events.length, events.length)) + 1
  let used = utf8Len('{"events":[]}') + reserved

  const sized = events.map((ev) => ({ ev, size: sizeOf(ev) + 1 })) // +1 = a vírgula
  const kept: T[] = []
  for (let i = sized.length - 1; i >= 0; i--) {
    const entry = sized[i]
    if (!entry) continue
    if (used + entry.size > budgetBytes) break
    used += entry.size
    kept.push(entry.ev)
  }
  kept.reverse() // varremos de trás pra frente; o batch sai em ordem cronológica

  return { events: [makeMarker(events.length - kept.length, kept.length), ...kept], dropped: events.length - kept.length }
}

/**
 * POST de fim de sessão (`visibilitychange → hidden` e `pagehide`). Mesmas
 * invariantes do `postTelemetry`: nunca lança, nunca re-tenta, nunca rotaciona
 * endpoint, nunca desloga.
 *
 * Onde ele difere do `postTelemetry` — e por quê:
 *
 *  - `keepalive: true` quando o browser tem (feature-detect). Sem isso o fetch
 *    morre junto com a página e o fim de sessão não chega.
 *  - SEM `yieldToMain()`: ele é um MACROTASK (MessageChannel) e a página pode
 *    morrer antes do próximo tick — o request nem chegaria a ser emitido. Ceder
 *    o frame aqui também não faz sentido: ninguém está mais olhando pra tela.
 *    Por isso a serialização é inline.
 *  - SEM AbortController/timeout: o timer é macrotask (não dispararia) e abortar
 *    um keepalive é exatamente o que ele existe pra impedir.
 *
 * O `await` do token é microtask no caso normal (token válido → retorna direto),
 * então o fetch ainda é emitido dentro do unload. Se o token estiver expirado, o
 * refresh é rede e a página pode morrer antes — perdemos o batch, que é o
 * contrato de fire-and-forget.
 */
export async function postTelemetryUnload(
  path: string,
  events: readonly unknown[],
  makeMarker: (dropped: number, kept: number) => unknown,
): Promise<boolean> {
  if (events.length === 0) return false
  try {
    const token = await telemetryAuthToken()
    if (!token) return false // sem identidade → descarta, sem barulho

    const keepalive = supportsKeepalive()
    // OS DOIS CAMINHOS TÊM TETO, e por motivos diferentes: com keepalive vale a quota de
    // 64 KiB do BROWSER; sem keepalive (TV em `hidden`, app vivo, fetch normal) vale o
    // teto do SERVIDOR.
    //
    // ⚠️ Este comentário afirmava "sem teto de tamanho" no segundo caso, e era verdade
    // até a rota ganhar `bodyLimit`. Deixar como estava faria o fim de sessão de um
    // buffer gordo levar 413 e voltar pro buffer — o lote é recuperado pelo flush
    // periódico (que fatia por bytes), então não vira loop, mas é um envio perdido à toa
    // no exato momento em que a sessão está acabando.
    // O orçamento é reportado junto com o corte (abaixo): com dois tetos possíveis, uma
    // mensagem que cita sempre o do keepalive mentiria metade das vezes.
    const orcamento = keepalive ? KEEPALIVE_BUDGET_BYTES : SERVER_BODY_BUDGET_BYTES
    const batch = fitKeepaliveBatch(events, makeMarker, orcamento)

    if (batch.dropped > 0 && import.meta.env.DEV) {
      console.warn(
        `[qoe] fim de sessão truncado: ${batch.dropped} eventos cortados pra caber ` +
          `em ${orcamento}B (${keepalive ? 'quota do keepalive' : 'teto de corpo do servidor'}) ` +
          `— rastro vai no batch como telemetry_truncated`,
      )
    }

    const headers: Record<string, string> = {
      'content-type': 'application/json',
      authorization: `Bearer ${token}`,
    }
    const res = await fetch(`${getApiBase()}${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ events: batch.events }),
      ...(keepalive ? { keepalive: true } : {}),
    })
    return res.ok
  } catch {
    return false
  }
}
